import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { idleTimeoutConfig } from 'src/app/const/constant';
import { AuthService } from './auth.service';

/**
 * TokenValidationService - Manages periodic token validation
 * 
 * Runs every 15 minutes to verify that the user's token is still valid
 * Triggers immediate logout if token becomes invalid during idle period
 */
@Injectable({
  providedIn: 'root',
})
export class TokenValidationService {
  private readonly CHECK_INTERVAL_MS = idleTimeoutConfig.checkInterval * 60 * 1000; // Convert to ms

  private validationTimerId: NodeJS.Timeout | null = null;
  private isTokenValid$ = new BehaviorSubject<boolean>(true);
  private validationSubscription: Subscription | null = null;

  constructor(private authService: AuthService) {}

  /**
   * Start periodic token validation
   * First check at T=0, then every 15 minutes
   */
  public startValidation(): void {
    console.log('[TokenValidation] Starting token validation checks - interval:', idleTimeoutConfig.checkInterval, 'minutes');
    
    // Perform initial check immediately
    this.performTokenCheck();

    // Schedule subsequent checks
    this.validationTimerId = setInterval(() => {
      this.performTokenCheck();
    }, this.CHECK_INTERVAL_MS);
  }

  /**
   * Stop periodic token validation
   */
  public stopValidation(): void {
    console.log('[TokenValidation] Stopping token validation checks');
    if (this.validationTimerId) {
      clearInterval(this.validationTimerId);
      this.validationTimerId = null;
    }
    if (this.validationSubscription) {
      this.validationSubscription.unsubscribe();
      this.validationSubscription = null;
    }
  }

  /**
   * Perform a single token validation check
   */
  private performTokenCheck(): void {
    console.log('[TokenValidation] Performing token validation check at', new Date().toLocaleTimeString());
    
    try {
      // Check if token is valid using auth service
      const isTokenValid = this.authService.isValidToken(
        'id_token' // Using standard token key
      );

      if (!isTokenValid) {
        console.warn('[TokenValidation] Token is invalid - triggering logout');
        this.isTokenValid$.next(false);
        
        // Force logout due to invalid token
        this.authService.forceLogout('invalid_token');
      } else {
        console.log('[TokenValidation] Token is valid - continuing session');
        this.isTokenValid$.next(true);
      }
    } catch (error) {
      console.error('[TokenValidation] Error during token validation:', error);
      // On validation error, assume token might be invalid - force logout for safety
      this.isTokenValid$.next(false);
      this.authService.forceLogout('invalid_token');
    }
  }

  /**
   * Get token validity state observable
   */
  public getTokenValidity$() {
    return this.isTokenValid$.asObservable();
  }

  /**
   * Check if token is currently valid
   */
  public isTokenValid(): boolean {
    return this.isTokenValid$.value;
  }

  /**
   * Cleanup on service destruction
   */
  ngOnDestroy(): void {
    this.stopValidation();
  }
}
